/**
 * Environment Configuration
 * Centralized configuration for different deployment environments
 */

// Environment detection - handle both Vite and Node.js environments
const getEnvironmentInfo = () => {
  // Check if we're in a Vite environment (import.meta.env available)
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    return {
      isDevelopment: import.meta.env.DEV || import.meta.env.NODE_ENV === 'development',
      isProduction: import.meta.env.PROD || import.meta.env.NODE_ENV === 'production',
      mode: import.meta.env.MODE
    };
  }
  
  // Fall back to Node.js environment variables
  const nodeEnv = process.env.NODE_ENV || 'development';
  return {
    isDevelopment: nodeEnv === 'development',
    isProduction: nodeEnv === 'production',
    mode: nodeEnv
  };
};

const envInfo = getEnvironmentInfo();
const isDevelopment = envInfo.isDevelopment;
const isProduction = envInfo.isProduction;

// Use console.log directly to avoid circular dependency with utilities.js
if (isDevelopment) {
  console.log('%c🔧 Config Environment Detection:', 'color: #2196F3; font-weight: bold', { 
    isDevelopment, 
    isProduction, 
    mode: envInfo.mode 
  });
}

const config = {
  development: {
    // API Configuration
    API_ENDPOINT: 'https://sheets.livepolls.app/api/spreadsheets/9664b907-bcb0-4e3a-8394-1d0b4adaba5e/Sheet1',
    
    // Debugging & Logging
    ENABLE_LOGGING: true,
    ENABLE_DEBUG_TOOLS: true,
    ENABLE_PERFORMANCE_MONITORING: true,
    
    // UI & UX Settings (more forgiving in dev)
    TOAST_DURATION: 5000,
    ERROR_TOAST_DURATION: 10000,
    NETWORK_TIMEOUT: 10000,
    API_RETRY_ATTEMPTS: 2,
    
    // Development-specific features
    SHOW_STATE_IN_CONSOLE: true,
    ENABLE_HOT_RELOAD: true,
    DETAILED_ERROR_MESSAGES: true,
    
    // Performance settings (less optimized for debugging)
    DEBOUNCE_DELAY: 100, // Shorter delay for development
    ANIMATION_DURATION: 300, // Slower animations for debugging
  },
  
  production: {
    // API Configuration
    API_ENDPOINT: 'https://sheets.livepolls.app/api/spreadsheets/9664b907-bcb0-4e3a-8394-1d0b4adaba5e/Sheet1',
    
    // Debugging & Logging
    ENABLE_LOGGING: false,
    ENABLE_DEBUG_TOOLS: false,
    ENABLE_PERFORMANCE_MONITORING: false,
    
    // UI & UX Settings (optimized for production)
    TOAST_DURATION: 3000,
    ERROR_TOAST_DURATION: 6000,
    NETWORK_TIMEOUT: 5000,
    API_RETRY_ATTEMPTS: 3,
    
    // Production-specific features
    SHOW_STATE_IN_CONSOLE: false,
    ENABLE_HOT_RELOAD: false,
    DETAILED_ERROR_MESSAGES: false,
    
    // Performance settings (optimized)
    DEBOUNCE_DELAY: 300,
    ANIMATION_DURATION: 200,
  },
  
  // Test environment (can be added later)
  test: {
    API_ENDPOINT: 'http://localhost:3000/api/test/cocktails',
    ENABLE_LOGGING: false,
    ENABLE_DEBUG_TOOLS: false,
    TOAST_DURATION: 100, // Fast for testing
    NETWORK_TIMEOUT: 2000,
    API_RETRY_ATTEMPTS: 1,
    SHOW_STATE_IN_CONSOLE: false,
    DETAILED_ERROR_MESSAGES: true, // Helpful for debugging tests
    DEBOUNCE_DELAY: 10, // Minimal delay for tests
    ANIMATION_DURATION: 0, // No animations in tests
  }
};

// Determine current environment
const currentEnv = isDevelopment ? 'development' : isProduction ? 'production' : 'development';

if (isDevelopment) {
  console.log('%c🌟 Current environment:', 'color: #4CAF50; font-weight: bold', currentEnv);
}

// Export the configuration for the current environment
const currentConfig = config[currentEnv];

// Add environment metadata
currentConfig.ENVIRONMENT = currentEnv;
currentConfig.IS_DEVELOPMENT = isDevelopment;
currentConfig.IS_PRODUCTION = isProduction;
currentConfig.BUILD_TIMESTAMP = new Date().toISOString();

// Validate configuration
const requiredKeys = [
  'API_ENDPOINT',
  'ENABLE_LOGGING',
  'TOAST_DURATION',
  'NETWORK_TIMEOUT',
  'API_RETRY_ATTEMPTS'
];

for (const key of requiredKeys) {
  if (currentConfig[key] === undefined) {
    const error = new Error(`Missing required configuration key: ${key}`);
    console.error('🔴 Configuration validation failed:', error);
    throw error;
  }
}

if (isDevelopment) {
  console.log('%c✅ Configuration loaded successfully:', 'color: #4CAF50; font-weight: bold', currentConfig);
}

// Make configuration available globally for debugging (development only)
if (isDevelopment && typeof window !== 'undefined') {
  window.appConfig = currentConfig;
  console.log('%c🔍 Configuration exposed to window.appConfig for debugging', 'color: #2196F3; font-weight: bold');
}

export default currentConfig;

// Export environment utilities
export const ENV = {
  isDevelopment,
  isProduction,
  current: currentEnv,
  
  // Helper functions
  ifDev: (devValue, prodValue = null) => isDevelopment ? devValue : prodValue,
  ifProd: (prodValue, devValue = null) => isProduction ? prodValue : devValue,
};

// Export all configurations for testing purposes
export const ALL_CONFIGS = config;
